import { useRollbar } from '@rollbar/react'
import axios, { CancelTokenSource } from 'axios'
import * as fileActions from 'client/actions/fileActions'
import * as fileApi from 'client/api/fileApi'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { DataFile } from 'client/store/files/filesReducer'
import { BadRequest } from 'common/errors/BadRequest'
import { Cancelled } from 'common/errors/Cancelled'
import { MissingContentLengthError } from 'common/errors/MissingContentLengthError'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import mime from 'mime'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { getHumanReadableSize, getMimeType, isImage } from '../utils'

interface UseFileUploaderProps {
  mimeTypes: string[]
  afterUpload: (file: DataFile) => void
  onUploading: (value: boolean) => void
  validateUploadFile?: (file: File, mimeType: string) => Promise<boolean>
  shouldOptimize?: boolean
}

const ALLOWED_OTHER_SIZE = 2147483648
const ALLOWED_IMAGE_SIZE = 5242880

export const useFileUploader = ({
  mimeTypes,
  afterUpload,
  onUploading,
  validateUploadFile,
  shouldOptimize = true,
}: UseFileUploaderProps) => {
  const dispatch = useDispatch()
  const { isTemplate } = usePageRouteDetector()
  const rollbar = useRollbar()
  const { t } = useTranslation(['client', 'common'])

  const inputRef = useRef<HTMLInputElement>(null)
  const [optimize, setOptimize] = useState(shouldOptimize)
  const [errors, setErrors] = useState<string[]>([])
  const [uploadState, setUploadState] = useState({ total: 1, loaded: 1 })
  const [uploadedFiles, setUploadedFiles] = useState<DataFile[]>([])
  const [uploadingFileId, setUploadingFileId] = useState<number | null>(null)
  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource>()

  const wasCancelledRef = useRef(false)
  const progressStartedRef = useRef(false)
  const loadedRef = useRef(0)
  let lastUpdate = 0

  const smoothProgress = (
    from: number,
    to: number,
    duration = 500,
  ): Promise<void> => {
    return new Promise(resolve => {
      if (from >= to) {
        resolve()
        return
      }

      const steps = 10
      const stepTime = duration / steps
      const delta = (to - from) / steps
      let current = from

      const interval = setInterval(() => {
        current += delta
        setUploadState({
          total: 100,
          loaded: Math.min(Math.round(current), 100),
        })

        if (current >= to) {
          clearInterval(interval)
          resolve()
        }
      }, stepTime)
    })
  }

  const createProgressHandler = (startPercent = 0, maxPercent = 90) => {
    return ({ total = 0, loaded = 0 }) => {
      progressStartedRef.current = true
      const now = Date.now()
      const throttleMs = 100

      if (now - lastUpdate >= throttleMs && total) {
        const rawPercent = (loaded / total) * 100

        const scaledPercent = Math.min(
          Math.round(
            (rawPercent * (maxPercent - startPercent)) / 100 + startPercent,
          ),
          maxPercent,
        )

        setUploadState({ total: 100, loaded: scaledPercent })
        loadedRef.current = scaledPercent
        lastUpdate = now
      }
    }
  }

  const progressHandlerUploaderFile = createProgressHandler(12, 85)
  const progressHandlerActivateFile = createProgressHandler(87, 98)

  const validate = (file: File) => {
    if (!file) {
      return false
    }

    const allowedSize = isImage(file.type)
      ? ALLOWED_IMAGE_SIZE
      : ALLOWED_OTHER_SIZE
    // 256 - (user_id/hash)
    if (file.name.length > 234) {
      // eslint-disable-next-line no-undef
      alert(t('core.file_manager.upload_long_file_name_warning'))
      return false
    }

    if (file.size > allowedSize) {
      // eslint-disable-next-line no-undef
      alert(
        t('core.file_manager.upload_max_size_warning', {
          size: getHumanReadableSize(allowedSize),
        }),
      )
      return false
    }

    const testType = (type: string) => (mimeType: string) =>
      RegExp(mimeType).test(type)
    const mimeType = mime.getType(file.name) || ''

    if (mimeTypes.some(testType(mimeType)) === false) {
      // eslint-disable-next-line no-undef
      alert(t('core.file_manager.file_not_supported'))
      return false
    }

    return true
  }

  const resetProgress = () => {
    setUploadState({ total: 100, loaded: 100 })
  }

  const cancelUpload = () => {
    wasCancelledRef.current = true

    if (!uploadingFileId) {
      setUploadedFiles([])
      onUploading(false)
      return
    }

    if (cancelTokenSource) {
      cancelTokenSource.cancel()
      resetProgress()

      fileApi.removeDraftFile(uploadingFileId).catch(err => {
        rollbar.error(err as Error)
      })

      setUploadedFiles(prev => prev.filter(file => file.id !== uploadingFileId))

      onUploading(false)
      setUploadingFileId(null)
    }
  }

  const handleUpload = async (file?: File) => {
    const cancelTokenSource = axios.CancelToken.source()
    setCancelTokenSource(cancelTokenSource)
    wasCancelledRef.current = false
    progressStartedRef.current = false

    try {
      setErrors([])
      onUploading(true)

      setTimeout(() => {
        if (!progressStartedRef.current && !wasCancelledRef.current) {
          smoothProgress(5, 10, 500)
        }
      }, 300)

      const mimeType = getMimeType(file?.name)

      if (file && validateUploadFile) {
        const validationResult = await validateUploadFile(file, mimeType || '')
        if (!validationResult) {
          return
        }
      }

      const {
        data: { uploadOptions, id },
      } = await fileApi.validateAndGetUploadOptions(file, isTemplate, mimeType)

      if (wasCancelledRef.current) {
        return
      }

      setUploadingFileId(id)

      await fileApi.uploadFile(
        file,
        uploadOptions,
        progressHandlerUploaderFile,
        cancelTokenSource.token,
      )

      if (wasCancelledRef.current) {
        return
      }

      const { data } = await fileApi.activateFile(
        id,
        optimize,
        progressHandlerActivateFile,
      )

      if (wasCancelledRef.current) {
        return
      }

      await smoothProgress(99, 100, 300)

      dispatch(fileActions.uploadFileSuccess(data))
      const uploadedFile = data[Object.keys(data)[0]]
      setUploadedFiles(prevFiles => [...prevFiles, uploadedFile])
      afterUpload(uploadedFile)
    } catch (e) {
      if (uploadingFileId) {
        fileApi.removeDraftFile(uploadingFileId)
      } else if (e instanceof NotFound) {
        // do nothing, draft file was removed
      } else if (wasCancelledRef.current) {
        // do nothing
      } else if (e instanceof NetworkError) {
        setErrors([t('core.errors.no_connection')])
        // do nothing
      } else if (e instanceof BadRequest) {
        if (e.response.data.errors && 'fileName' in e.response.data.errors) {
          if (e.response.data.errors.fileName) {
            setErrors(Object.values(e.response.data.errors) as string[])
          }
          if ('mimeType' in e.response.data.errors) {
            setErrors([t('core.file_manager.upload.not_supported_file_type')])
          }
        } else if (typeof e.response.data === 'string') {
          setErrors([t('core.file_manager.upload.upload_failed_by_timeout')])
        } else {
          setErrors([t('core.file_manager.upload.server_error')])
        }
      } else if (e instanceof MissingContentLengthError) {
        if (e.response.data) {
          rollbar.error('Upload to S3 failed 411', {
            data: e.response.data,
          })
          setErrors([t('core.file_manager.upload.blocked')])
        }
      } else if (e instanceof Cancelled) {
        // do nothing
      } else {
        setErrors([t('core.file_manager.upload.server_error')])
      }
    } finally {
      setUploadingFileId(null)
      onUploading(false)
    }
  }

  const handleFiles = async () => {
    const file = inputRef?.current?.files?.[0]
    if (file && !validate(file)) {
      inputRef.current.value = ''
      return
    }

    await handleUpload(file)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const openFileSelection = () => {
    inputRef?.current?.click()
  }

  return {
    inputRef,
    optimize,
    setOptimize,
    errors,
    uploadedFiles,
    uploadingFileId,
    cancelUpload,
    uploadState,
    handleFiles,
    openFileSelection,
    setUploadedFiles,
    handleUpload,
    validate,
  }
}
